import React, {forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState} from "react";
import {CardElement, useElements, useStripe} from '@stripe/react-stripe-js';
import ToastService from "../../../../../core/services/toast";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {Box, TextField, useColorScheme, useTheme} from "@mui/material";
import Form, {MuiRFF, Yup} from "../../../form";
import './index.scss';
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import Api from "../../../../../core/services/api/api";
import {CreditCardProviders} from "../../../../../core/constants/enums";
import useAccessLevels from "../../../../hooks/use-access-levels";

const schema = Yup.object().shape({
    name: Yup.string().required(ValidateMessages.required),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const NewStripeCreditCardForm = forwardRef(({
                                                id,
                                                className,
                                                onSuccess,
                                                customerId,
                                                setSubmitting,
                                                getApiHeaders,
                                                bypassAuthCheck = false,
                                                logger,
                                                submitFunction,
                                            }, ref) => {
    const [focusedElement, setFocusedElement] = useState(null);
    const stripe = useStripe();
    const elements = useElements();
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canManagePaymentMethods = packages.sales.manageCustomerPaymentMethods;
    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();

    const focused = useMemo(() => !!focusedElement, [focusedElement]);
    const disabled = useMemo(() => !stripe || !elements, [stripe, elements]);


    /**
     * Attaches a [submit] callback to the passed reference to this component so that the parent component can
     * submit this form using their reference.
     */
    useImperativeHandle(ref, () => ({
        submit: () => formRef.current.submit(),
    }), [formRef])

    /**
     * Handles the form submission event
     */
    const handleSubmit = useCallback(async (values) => {
        if (disabled)
            return;
        if (!bypassAuthCheck && !canManagePaymentMethods)
            return;
        setSubmitting(true);
        const result = await stripe.createPaymentMethod({
            elements,
            params: {
                billing_details: {
                    name: values.name,
                },
            },
        });
        if (!isMounted())
            return;
        setSubmitting(false);
        if (result.error) {
            ToastService.error({
                title: result.error.code.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                message: result.error.message,
            });
            return;
        }
        const forApi = {
            lastFourDigits: result.paymentMethod.card.last4?.toString(),
            month: result.paymentMethod.card.exp_month?.toString(),
            type: result.paymentMethod.card.brand?.toString(),
            holderName: values.name,
            issuerId: '',
            token: result.paymentMethod.id,
            customerId: customerId,
            paymentProvider: CreditCardProviders.stripe,
        }
        const response = submitFunction ? await submitFunction(forApi) : await Api.addCustomerCreditCardByTokenV2(forApi, getApiHeaders, bypassAuthCheck, logger);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            onSuccess({
                ...response.data,
                providerName: CreditCardProviders.stripe,
            });
        }
    }, [disabled, setSubmitting, stripe, elements, isMounted, customerId, submitFunction, getApiHeaders, bypassAuthCheck, canManagePaymentMethods, logger, onSuccess])


    const colorScheme = useColorScheme()
    const darkMode = useMemo(() => colorScheme.mode === 'dark' || (colorScheme.mode === 'system' && colorScheme.systemMode === 'dark'), [colorScheme]);
    const theme = useTheme();

    /**@type {import('@stripe/stripe-js').StripeCardElementOptions}*/
    const stripeOptions = useMemo(() => ({
        classes: {
            base: 'stripe-card-element-base',
            complete: 'stripe-card-element-complete',
            empty: 'stripe-card-element-empty',
            focus: 'stripe-card-element-focus',
            invalid: 'stripe-card-element-invalid',
        },
        style: {
            base: {
                color: darkMode ? theme.palette.text.primary : theme.palette.text.primary,
                fontFamily: 'Open Sans, sans-serif',
                fontSize: '16px',
                '::placeholder': {
                    color: darkMode ? theme.palette.text.secondary : theme.palette.text.secondary,
                },
            },
            invalid: {
                color: theme.palette.error.main,
            },

        },
    }), [darkMode, theme.palette.error.main, theme.palette.text.primary, theme.palette.text.secondary]);

    return (
        <>
            <Form
                onSubmit={handleSubmit}
                formProps={{
                    id: id,
                    className: className,
                }}
                validate={validate}
            >
                {({form}) => {
                    formRef.current = form;
                    return (
                        <>
                            <MuiRFF.TextField
                                label={'Holder Name'}
                                name={'name'}
                                required={required.name}
                                sx={{
                                    marginBlock: 2,
                                }}
                            />
                            <Box sx={{position: 'relative', width: '100%', height: 53.33}}>
                                <TextField
                                    value={''}
                                    focused={focused}
                                    label={focused ? "Card Information" : ''}
                                    tabIndex={-1}
                                    InputProps={{inputProps: {tabIndex: -1}}}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                    }}
                                />
                                <CardElement
                                    onFocus={(e) => setFocusedElement(e.elementType)}
                                    onBlur={() => setFocusedElement(null)}
                                    options={stripeOptions}
                                />
                            </Box>
                        </>
                    );
                }}
            </Form>
        </>
    )
});

export default NewStripeCreditCardForm;